import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Drawer } from "@/components/ui/drawer";
import { Pencil, Trash, Plus, Search, X, Settings } from "lucide-react";
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { getApiUrl } from "@/utils/api";
import { toast } from "react-toastify";
import { DeleteModal } from "@/components/delete-modal";
import { useTranslation } from "react-i18next";
import { Pagination } from "@/components/pagination";
import { SupplierForm } from "./SupplierForm";
import { PageHeader } from "@/components/PageHeader";

interface Supplier {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const SuppliersList = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  const PAGE_SIZE = 10;

  // Fetch suppliers
  const { data, isLoading, error, refetch } = useQuery<ApiResponse<Supplier>>({
    queryKey: [
      "suppliers",
      currentPage,
      isSearchActive ? searchQuery : undefined,
    ],
    queryFn: async () => {
      const offset = (currentPage - 1) * PAGE_SIZE;
      let url = `comercializadoras/?limit=${PAGE_SIZE}&offset=${offset}`;

      if (isSearchActive && searchQuery) {
        url += `&name=${encodeURIComponent(searchQuery)}`;
      }

      const response = await fetch(getApiUrl(url), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(t("suppliers.error.fetch"));
      }
      return response.json();
    },
    enabled: !!token,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(getApiUrl(`comercializadoras/${id}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || t("suppliers.error.delete"));
      }
      return id; // Return the deleted ID for use in onSuccess
    },
    onSuccess: (deletedId) => {
      // Invalidate and refetch the list
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });

      // Show success message
      toast.success(t("common.success.deleted")); // Ensured react-toastify is used

      // Only close the modal after successful deletion
      setItemToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("common.error"));
    },
  });

  const handleAdd = () => {
    setEditingId(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingId(id);
    setIsDrawerOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setIsSearchActive(true);
      setCurrentPage(1);
    }
  };

  const handleResetSearch = () => {
    setSearchQuery("");
    setIsSearchActive(false);
    setCurrentPage(1);
  };

  // Reset search when search query becomes empty
  useEffect(() => {
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
    }
  }, [searchQuery]);

  const handleDelete = () => {
    if (itemToDelete) {
      deleteMutation.mutate(itemToDelete);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500">
        {t("suppliers.error.loading")}: {error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full pb-8">
      <PageHeader
        icon={<Settings className="w-12 h-12 text-primary-foreground" />}
        actionTitle="suppliers.add"
        title="suppliers.title"
        description="suppliers.description"
        handleNew={handleAdd}
      />

      <div className="flex items-center space-x-2 p-4">
        <div className="relative">
          <Input
            placeholder={t("common.search")}
            className="w-[400px] pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        </div>
        <Button
          variant={isSearchActive ? "outline" : "default"}
          onClick={isSearchActive ? handleResetSearch : handleSearch}
          disabled={!searchQuery.trim() && !isSearchActive}
        >
          {isSearchActive ? (
            <>
              <X className="mr-2 h-4 w-4" />
              {t("common.reset")}
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              {t("common.search")}
            </>
          )}
        </Button>
      </div>

      <div className="px-4">
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("suppliers.table.id")}</TableHead>
                <TableHead>{t("suppliers.table.name")}</TableHead>
                <TableHead className="text-right">
                  {t("common.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.results?.length ? (
                data.results.map((supplier) => (
                  <TableRow key={supplier.id}>
                    <TableCell variant="compact" className="font-medium">
                      {supplier.id}
                    </TableCell>
                    <TableCell variant="compact" className="font-medium">
                      {supplier.name}
                    </TableCell>
                    <TableCell
                      variant="compact"
                      className="text-right space-x-2"
                    >
                      <div className="flex justify-center items-center">
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => handleEdit(supplier.id)}
                        >
                          <Pencil
                            size={30}
                            fill="#000000"
                            stroke="#ffffff"
                            strokeWidth={1}
                          />
                        </div>
                        <div
                          role="button"
                          className="w-[30px] w-[40px] p-1 hover:bg-[#A3F800] rounded-md"
                          onClick={() => setItemToDelete(supplier.id)}
                        >
                          <Trash
                            size={30}
                            fill="#ff0000"
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {t("suppliers.noData")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {data && data.count > PAGE_SIZE && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(data.count / PAGE_SIZE)}
            hasNextPage={!!data.next}
            hasPreviousPage={!!data.previous}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <div className="max-h-[80vh] overflow-y-auto p-6">
            <DrawerHeader>
              <DrawerTitle>
                {editingId ? t("suppliers.editTitle") : t("suppliers.addTitle")}
              </DrawerTitle>
              <DrawerDescription>
                {editingId
                  ? t("suppliers.editDescription")
                  : t("suppliers.addDescription")}
              </DrawerDescription>
            </DrawerHeader>
            <div className="mt-4">
              <SupplierForm
                id={editingId}
                onSuccess={() => {
                  setIsDrawerOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["suppliers"] });
                }}
                onCancel={() => setIsDrawerOpen(false)}
              />
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <DeleteModal
        open={!!itemToDelete}
        onOpenChange={(open) => !open && setItemToDelete(null)}
        onDelete={handleDelete}
        title={t("suppliers.deleteTitle")}
        entity={t("suppliers.title")}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};
